import { Box, Button, Chip, Divider, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography, useMediaQuery, useTheme } from '@mui/material'
import ApproveIcon from '@mui/icons-material/ApprovalOutlined'
import ReceiptIcon from '@mui/icons-material/ReceiptOutlined'
import PersonIcon from '@mui/icons-material/Person'
import RejectIcon from '@mui/icons-material/HighlightOffOutlined'

function EmiApproveOrReject(
    { loadingCorrection, corrections, handleApprove, handleReject }:
        {
            loadingCorrection: boolean, corrections: any[],
            handleApprove: (_id: string) => void, handleReject: (_id: string) => void;
        }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (corrections.length === 0) {
        return (
            <Paper elevation={2} sx={{ p: 3, mt: 4, mb: 5, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ReceiptIcon color="action" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="h2">
                        Pending Repayment Corrections
                    </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Box
                    sx={{
                        textAlign: 'center',
                        py: 4,
                        backgroundColor: 'grey.50',
                        borderRadius: 2
                    }}
                >
                    <Typography variant="body1" color="text.secondary">
                        No pending correction requests
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        All requests have been processed
                    </Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mt: 4, mb: 5, borderRadius: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ReceiptIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" component="h2">
                    Pending Repayment Corrections
                </Typography>
                <Chip
                    label={`${corrections.length} pending`}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ ml: 2 }}
                />
            </Box>
            <Divider sx={{ mb: 3 }} />

            {/* Mobile Cards View */}
            {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {corrections.map((c) => (
                        <Paper
                            key={c._id}
                            variant="outlined"
                            sx={{ p: 2, borderRadius: 2 }}
                        >
                            {/* Receipt Number */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <ReceiptIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                <Typography variant="subtitle2" fontWeight="600">
                                    Receipt: {c.repayment.receiptNumber}
                                </Typography>
                            </Box>

                            {/* Amounts */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Old Amount
                                    </Typography>
                                    <Typography variant="body2" fontWeight="500" color="error">
                                        ₹{c.oldAmount}
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        →
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        New Amount
                                    </Typography>
                                    <Typography variant="body2" fontWeight="500" color="success.main">
                                        ₹{c.newAmount}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Reason */}
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Reason
                                </Typography>
                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                    {c.reason}
                                </Typography>
                            </Box>

                            {/* Requested By */}
                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <PersonIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                    <Typography variant="caption" color="text.secondary">
                                        Requested By
                                    </Typography>
                                </Box>
                                <Typography variant="body2">
                                    {c.requestedBy.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {c.requestedBy.block?.name} block
                                </Typography>
                            </Box>

                            {/* Actions */}
                            <Box sx={{
                                display: 'flex',
                                gap: 1,
                                justifyContent: isSmallMobile ? 'space-between' : 'flex-end'
                            }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleReject(c._id)}
                                    disabled={loadingCorrection}
                                    startIcon={isSmallMobile ? null : <RejectIcon />}
                                    sx={{ minWidth: isSmallMobile ? 'auto' : null }}
                                >
                                    {isSmallMobile ? <RejectIcon /> : 'Reject'}
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    onClick={() => handleApprove(c._id)}
                                    disabled={loadingCorrection}
                                    startIcon={isSmallMobile ? null : <ApproveIcon />}
                                    sx={{ minWidth: isSmallMobile ? 'auto' : null }}
                                >
                                    {isSmallMobile ? <ApproveIcon /> : 'Approve'}
                                </Button>
                            </Box>
                        </Paper>
                    ))}
                </Box>
            ) : (
                /* Desktop Table View */
                <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Receipt No</TableCell>
                            <TableCell>Amount Change</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell>Requested By</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {corrections.map((c) => (
                            <TableRow
                                key={c._id}
                                hover
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <ReceiptIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                        <Typography variant="body2" fontWeight="500">
                                            {c.repayment.receiptNumber}
                                        </Typography>
                                    </Box>
                                </TableCell>

                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" color="error">
                                            ₹{c.oldAmount}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            →
                                        </Typography>
                                        <Typography variant="body2" color="success.main" fontWeight="500">
                                            ₹{c.newAmount}
                                        </Typography>
                                    </Box>
                                </TableCell>

                                <TableCell sx={{ maxWidth: 200 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            wordBreak: 'break-word',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {c.reason}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Box>
                                        <Typography variant="body2">{c.requestedBy.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {c.requestedBy.block?.name} block
                                        </Typography>
                                    </Box>
                                </TableCell>

                                <TableCell align="center">
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="success"
                                            onClick={() => handleApprove(c._id)}
                                            disabled={loadingCorrection}
                                            startIcon={<ApproveIcon />}
                                        >
                                            Approve
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={() => handleReject(c._id)}
                                            disabled={loadingCorrection}
                                            startIcon={<RejectIcon />}
                                        >
                                            Reject
                                        </Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            {/* Loading Overlay */}
            {loadingCorrection && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 2,
                        zIndex: 1
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Processing...
                    </Typography>
                </Box>
            )}
        </Paper>
    );
}

export default EmiApproveOrReject